"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiColumns, FiDownload, FiLayers, FiSearch, FiX } from "react-icons/fi";
import { HiOutlinePhotograph } from "react-icons/hi";
import { LuLoader2 } from "react-icons/lu";
import { RiFocus3Line } from "react-icons/ri";
import Image from "next/image";
import { LazyImage } from "@/app/[tenant]/(console)/scribe/playground/[id]/attachment-item";
import PatientSearch from "@/components/patient/patient-search";
import { BackdropSwatches } from "@/components/photo-studio/backdrop-picker";
import { MarketingExportButton } from "@/components/photo-studio/marketing/marketing-export-button";
import { Badge, Button, Card, Input } from "@/components/ui";
import { Dialog, DialogBody, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImageTransformControls } from "@/components/ui/image-transform-controls";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { queryClient } from "@/config/react-query";
import { useUser } from "@/contexts/user-provider";
import { dotMarkRadius, markShapeOf, xMarkSegments } from "@/lib/annotation-mark-shape";
import { medicationNumberFontSize, medicationNumberOutline } from "@/lib/annotation-number";
import { pinAnchorRadius, pinBadgeCornerRadius, pinBadgeFontSize, pinBadgeSize, pinBadgeTextColor, pinLabelText, pinLeaderEnd } from "@/lib/annotation-pin";
import { isDashed } from "@/lib/annotation-stroke";
import { detectPose, getFaceLandmarker, loadDetectableImage } from "@/lib/face-align";
import { formatDate, formatName, toClinicDateKey } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { alignPair } from "@/lib/photo-studio/align-pair";
import { ensureStudioMask, isStudioImagePath } from "@/lib/photo-studio/studio-photo";
import { renderComparisonToCanvas } from "@/lib/render-comparison-canvas";
import { reportScribeAttachmentLoaded } from "@/lib/scribe-attachment-analytics";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import type { StrokeStyle } from "@/types/annotation";
import { Annotation } from "@/types/annotation";
import { ImageTransform, NoteAttachment } from "@/types/notes";
import { PatientSummary } from "@/types/patient";
import type { PairAlignment, StudioBackdrop } from "@/types/photo-studio";
import { photoStudioQueryOptions, usePhotoStudioSettings, useStudioRender } from "@/api/provider/photo-studio/studio-query";
import { noteQueryKeys } from "@/api/provider/scribe/note-query";
import { addNoteAttachment } from "@/api/provider/scribe/notes";

// Pin geometry is scaled into the marks layer's units, where the photo's short side is 1.
const COMPARE_PIN_SCALE = 0.0032;
// Line width per unit of strokeWidth, matching the Gallery thumbnail (0.8 in a 200px box).
const COMPARE_LINE_WIDTH = 0.004;

// Dashes scale with the line so a dashed mark reads the same at any size.
const compareDashArray = (strokeStyle: StrokeStyle | undefined, strokeWidth: number): string | undefined => isDashed(strokeStyle) ? `${strokeWidth * 3} ${strokeWidth * 2}` : undefined;

// The marks layer draws in a box shaped like the photo, short side 1, so it
// lines up with the photo under `object-contain` and radii stay a fraction of
// the short side, as in the editor.
const photoBox = (aspectRatio: number) => aspectRatio >= 1 ? {
  width: aspectRatio,
  height: 1
} : {
  width: 1,
  height: 1 / aspectRatio
};
const toPhotoBox = (annotation: Annotation, width: number, height: number): Annotation => {
  const at = (point: {
    x: number;
    y: number;
  }) => ({
    x: point.x * width,
    y: point.y * height
  });
  switch (annotation.type) {
    case "pen":
    case "syringeLine":
      return {
        ...annotation,
        points: annotation.points.map(at)
      };
    case "arrow":
      return {
        ...annotation,
        start: at(annotation.start),
        end: at(annotation.end)
      };
    case "circle":
      return {
        ...annotation,
        center: at(annotation.center)
      };
    case "comment":
    case "dot":
      return {
        ...annotation,
        position: at(annotation.position)
      };
    case "pin":
      return {
        ...annotation,
        position: at(annotation.position),
        label: at(annotation.label)
      };
    case "medication":
      return {
        ...annotation,
        position: annotation.position && at(annotation.position),
        start: annotation.start && at(annotation.start),
        end: annotation.end && at(annotation.end)
      };
  }
};
const usePhotoAspectRatio = url => {
  const $ = _c(3);
  const [aspectRatio, setAspectRatio] = useState();
  let t0;
  let t1;
  if ($[0] !== url) {
    t0 = () => {
      setAspectRatio(undefined);
      const img = new window.Image();
      img.onload = () => setAspectRatio(img.naturalWidth / img.naturalHeight);
      img.src = url;
      return () => {
        img.onload = null;
      };
    };
    t1 = [url];
    $[0] = url;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return aspectRatio;
};

// Default transform values
const DEFAULT_TRANSFORM: ImageTransform = {
  panX: 0,
  panY: 0,
  zoom: 1,
  rotation: 0,
  cropX: null,
  cropY: null,
  cropWidth: null,
  cropHeight: null,
  mirror: false
};

// Check if transform has any changes from default
const hasTransformChanges = (transform: ImageTransform): boolean => {
  return transform.panX !== 0 || transform.panY !== 0 || transform.zoom !== 1 || (transform.rotation ?? 0) !== 0 || transform.cropX !== null || transform.mirror === true;
};

// Check if transform has a crop applied
const hasCropApplied = (transform: ImageTransform): boolean => {
  return transform.cropX != null && transform.cropY != null && transform.cropWidth != null && transform.cropHeight != null && (transform.cropWidth ?? 0) > 0.01 && (transform.cropHeight ?? 0) > 0.01;
};

// Generate CSS styles to preview a transform
// This mirrors what the backend does: mirror first, then crop, then zoom, then pan, then rotate
const getTransformStyle = (transform: ImageTransform): React.CSSProperties => {
  const hasCrop = hasCropApplied(transform);
  const cropX = transform.cropX ?? 0;
  const cropY = transform.cropY ?? 0;
  const cropW = transform.cropWidth ?? 1;
  const cropH = transform.cropHeight ?? 1;
  const mirrorScale = transform.mirror ? -1 : 1;
  const rotation = transform.rotation ?? 0;
  if (hasCrop) {
    // When there's a crop, we need to:
    // 1. Scale up so the crop region fills the container
    // 2. Position so the crop region is centered
    // 3. Apply additional zoom and pan on top
    // 4. Apply rotation
    const baseScale = 1 / Math.min(cropW, cropH);
    const totalZoom = baseScale * transform.zoom;

    // Calculate position to center the crop region
    // The crop center in the original image coordinates
    const cropCenterX = cropX + cropW / 2;
    const cropCenterY = cropY + cropH / 2;

    // Offset from image center (0.5, 0.5) to crop center
    const offsetX = (0.5 - cropCenterX) * 100;
    const offsetY = (0.5 - cropCenterY) * 100;

    // Add pan offset (pan is relative to canvas, which is now the crop region)
    const panOffsetX = transform.panX * 100;
    const panOffsetY = transform.panY * 100;
    return {
      transform: `rotate(${rotation}deg) scaleX(${mirrorScale}) scale(${totalZoom}) translate(${offsetX + panOffsetX}%, ${offsetY + panOffsetY}%)`,
      transformOrigin: "center center"
    };
  }

  // No crop - just apply zoom, pan, mirror, and rotation
  // Pan values are -1 to 1 where 1 = full canvas width, matching backend's pan_x * canvas_width
  return {
    transform: `rotate(${rotation}deg) scaleX(${mirrorScale}) scale(${transform.zoom}) translate(${transform.panX * 100}%, ${transform.panY * 100}%)`,
    transformOrigin: "center center"
  };
};
type CompareMode = "slider" | "side-by-side";

// The frames' layout: side-by-side splits the area in two around an 8px gap
// (`gap-2`); the overlay uses the whole area for both photos.
const SIDE_BY_SIDE_GAP_PX = 8;

// A PairAlignment (scale, then rotate, then move, about the frame centre) as
// this modal's CSS transform, which applies rotate(scale(translate(p))): the
// move happens first in the image's own units, so undo the rotation and scale.
const alignmentToTransform = (alignment: PairAlignment, frame: {
  width: number;
  height: number;
}): ImageTransform => {
  const radians = alignment.rotationDeg * Math.PI / 180;
  const moveX = alignment.dx * frame.width;
  const moveY = alignment.dy * frame.height;
  const localX = moveX * Math.cos(radians) + moveY * Math.sin(radians);
  const localY = -moveX * Math.sin(radians) + moveY * Math.cos(radians);
  return {
    ...DEFAULT_TRANSFORM,
    zoom: alignment.scale,
    rotation: alignment.rotationDeg,
    panX: localX / alignment.scale / frame.width,
    panY: localY / alignment.scale / frame.height
  };
};

// Annotation overlay component for comparison mode
// Renders annotations on top of images with the same transform styles
const ComparisonAnnotationOverlay = t0 => {
  const $ = _c(18);
  const {
    annotations,
    imageUrl,
    style
  } = t0;
  const aspectRatio = usePhotoAspectRatio(imageUrl);
  if (!annotations || annotations.length === 0 || !aspectRatio) {
    return null;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== annotations || $[1] !== aspectRatio || $[2] !== style) {
    const box = photoBox(aspectRatio);
    t5 = "pointer-events-none absolute inset-0 overflow-hidden";
    t6 = style;
    t1 = "h-full w-full";
    t2 = `0 0 ${box.width} ${box.height}`;
    t3 = "xMidYMid meet";
    t4 = annotations.map(original => {
      const annotation = toPhotoBox(original, box.width, box.height);
      switch (annotation.type) {
        case "pen":
          {
            const pathData_0 = annotation.points.map(_temp).join(" ");
            return <path key={annotation.id} d={pathData_0} stroke={annotation.color} strokeWidth={annotation.strokeWidth * COMPARE_LINE_WIDTH} strokeDasharray={compareDashArray(annotation.strokeStyle, annotation.strokeWidth * COMPARE_LINE_WIDTH)} fill="none" strokeLinecap="round" strokeLinejoin="round" />;
          }
        case "arrow":
          {
            const startX_0 = annotation.start.x;
            const startY_0 = annotation.start.y;
            const endX_0 = annotation.end.x;
            const endY_0 = annotation.end.y;
            const angle_0 = Math.atan2(endY_0 - startY_0, endX_0 - startX_0);
            const arrowAngle_0 = Math.PI / 6;
            const arrowHead1X_0 = endX_0 - 0.025 * Math.cos(angle_0 - arrowAngle_0);
            const arrowHead1Y_0 = endY_0 - 0.025 * Math.sin(angle_0 - arrowAngle_0);
            const arrowHead2X_0 = endX_0 - 0.025 * Math.cos(angle_0 + arrowAngle_0);
            const arrowHead2Y_0 = endY_0 - 0.025 * Math.sin(angle_0 + arrowAngle_0);
            return <g key={annotation.id}><line x1={startX_0} y1={startY_0} x2={endX_0} y2={endY_0} stroke={annotation.color} strokeWidth={annotation.strokeWidth * COMPARE_LINE_WIDTH} strokeDasharray={compareDashArray(annotation.strokeStyle, annotation.strokeWidth * COMPARE_LINE_WIDTH)} strokeLinecap="round" /><path d={`M ${arrowHead1X_0} ${arrowHead1Y_0} L ${endX_0} ${endY_0} L ${arrowHead2X_0} ${arrowHead2Y_0}`} stroke={annotation.color} strokeWidth={annotation.strokeWidth * COMPARE_LINE_WIDTH} fill="none" strokeLinecap="round" strokeLinejoin="round" /></g>;
          }
        case "circle":
          {
            const shape = markShapeOf(annotation);
            if (shape === "dot") {
              return <circle key={annotation.id} cx={annotation.center.x} cy={annotation.center.y} r={dotMarkRadius(annotation.radius)} fill={annotation.color} />;
            }
            if (shape === "x") {
              return <g key={annotation.id}>{xMarkSegments(annotation.center.x, annotation.center.y, annotation.radius).map((t7, i) => {
                  const [x1, y1, x2, y2] = t7;
                  return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={annotation.color} strokeWidth={annotation.strokeWidth * COMPARE_LINE_WIDTH} strokeDasharray={compareDashArray(annotation.strokeStyle, annotation.strokeWidth * COMPARE_LINE_WIDTH)} strokeLinecap="round" />;
                })}</g>;
            }
            return <circle key={annotation.id} cx={annotation.center.x} cy={annotation.center.y} r={annotation.radius} stroke={annotation.color} strokeWidth={annotation.strokeWidth * COMPARE_LINE_WIDTH} strokeDasharray={compareDashArray(annotation.strokeStyle, annotation.strokeWidth * COMPARE_LINE_WIDTH)} fill="none" />;
          }
        case "comment":
          {
            const fontSize_0 = 0.02 + (annotation.strokeWidth || 1) * 0.005;
            const dotRadius = 0.008 + (annotation.strokeWidth || 1) * 0.002;
            return <g key={annotation.id}><circle cx={annotation.position.x} cy={annotation.position.y} r={dotRadius} fill={annotation.color} stroke="white" strokeWidth={0.002} /><text x={annotation.position.x + dotRadius + 0.01} y={annotation.position.y + fontSize_0 / 3} fontSize={fontSize_0} fill={annotation.color} fontWeight="500">{annotation.text.length > 20 ? annotation.text.substring(0, 20) + "..." : annotation.text}</text></g>;
          }
        case "dot":
          {
            const radius = annotation.radius ?? 0.01;
            return <circle key={annotation.id} cx={annotation.position.x} cy={annotation.position.y} r={radius} fill={annotation.color} stroke="white" strokeWidth={0.002} />;
          }
        case "pin":
          {
            const labelText = pinLabelText(annotation);
            const badge = pinBadgeSize(labelText, COMPARE_PIN_SCALE);
            const leaderEnd = pinLeaderEnd(annotation.position, annotation.label, badge);
            return <g key={annotation.id}><line x1={annotation.position.x} y1={annotation.position.y} x2={leaderEnd.x} y2={leaderEnd.y} stroke={annotation.color} strokeWidth={Math.max(annotation.strokeWidth * 0.5, 1) * COMPARE_LINE_WIDTH} strokeDasharray={compareDashArray(annotation.strokeStyle, Math.max(annotation.strokeWidth * 0.5, 1) * COMPARE_LINE_WIDTH)} strokeLinecap="round" /><circle cx={annotation.position.x} cy={annotation.position.y} r={pinAnchorRadius(COMPARE_PIN_SCALE)} fill={annotation.color} stroke="white" strokeWidth={0.002} /><rect x={annotation.label.x - badge.width / 2} y={annotation.label.y - badge.height / 2} width={badge.width} height={badge.height} rx={pinBadgeCornerRadius(COMPARE_PIN_SCALE)} fill={annotation.color} /><text x={annotation.label.x} y={annotation.label.y} fontSize={pinBadgeFontSize(COMPARE_PIN_SCALE)} fill={pinBadgeTextColor(annotation.color)} textAnchor="middle" dominantBaseline="central" fontWeight="bold">{labelText}</text></g>;
          }
        case "syringeLine":
          {
            const pathData = annotation.points.map(_temp2).join(" ");
            return <path key={annotation.id} d={pathData} stroke={annotation.color} strokeWidth={(annotation.strokeWidth + 1) * COMPARE_LINE_WIDTH} strokeDasharray={compareDashArray(annotation.strokeStyle, (annotation.strokeWidth + 1) * COMPARE_LINE_WIDTH)} fill="none" strokeLinecap="round" strokeLinejoin="round" />;
          }
        case "medication":
          {
            const amountText = String(annotation.amount);
            const fontSize = medicationNumberFontSize(annotation.strokeWidth, COMPARE_PIN_SCALE);
            if (annotation.shape === "circle" && annotation.position) {
              return <g key={annotation.id}><text x={annotation.position.x} y={annotation.position.y} fontSize={fontSize} fill={annotation.color} stroke="white" strokeWidth={medicationNumberOutline(fontSize)} paintOrder="stroke" textAnchor="middle" dominantBaseline="central" fontWeight="bold">{amountText}</text></g>;
            } else {
              if (annotation.shape === "arrow" && annotation.start && annotation.end) {
                const startX = annotation.start.x;
                const startY = annotation.start.y;
                const endX = annotation.end.x;
                const endY = annotation.end.y;
                const angle = Math.atan2(endY - startY, endX - startX);
                const arrowLength = 0.015 + (annotation.strokeWidth || 1) * 0.005;
                const arrowAngle = Math.PI / 6;
                const arrowHead1X = endX - arrowLength * Math.cos(angle - arrowAngle);
                const arrowHead1Y = endY - arrowLength * Math.sin(angle - arrowAngle);
                const arrowHead2X = endX - arrowLength * Math.cos(angle + arrowAngle);
                const arrowHead2Y = endY - arrowLength * Math.sin(angle + arrowAngle);
                return <g key={annotation.id}><line x1={startX} y1={startY} x2={endX} y2={endY} stroke={annotation.color} strokeWidth={Math.max(annotation.strokeWidth * COMPARE_LINE_WIDTH, 0.002)} strokeLinecap="round" /><path d={`M ${arrowHead1X} ${arrowHead1Y} L ${endX} ${endY} L ${arrowHead2X} ${arrowHead2Y} Z`} stroke={annotation.color} fill={annotation.color} strokeWidth={Math.max(annotation.strokeWidth * 0.002, 0.001)} /><text x={startX} y={startY} fontSize={fontSize} fill={annotation.color} stroke="white" strokeWidth={medicationNumberOutline(fontSize)} paintOrder="stroke" textAnchor="middle" dominantBaseline="central" fontWeight="bold">{amountText}</text></g>;
              }
            }
            return null;
          }
        default:
          {
            return null;
          }
      }
    });
    $[0] = annotations;
    $[1] = aspectRatio;
    $[2] = style;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t7 = <svg className={t1} viewBox={t2} preserveAspectRatio={t3}>{t4}</svg>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t6 || $[16] !== t7) {
    t8 = <div className={t5} style={t6}>{t7}</div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};
interface ImageCompareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceImage: NoteAttachment | null;
  availableImages: NoteAttachment[];
  patientId?: string;
  // The gallery paginates one local day per page, so the picker has to be able
  // to pull older days itself instead of stopping at whatever the parent loaded.
  hasMoreImages?: boolean;
  isLoadingMoreImages?: boolean;
  onLoadMoreImages?: () => void;
}

// Slider component for image overlay comparison
const ImageSliderOverlay = t0 => {
  const $ = _c(73);
  const {
    sourceUrl,
    compareUrl,
    sourceName,
    compareName,
    onPositionChange,
    beforeTransform,
    afterTransform,
    syncZoom,
    onSyncZoomChange,
    syncPan,
    onSyncPanChange,
    beforeAnnotations,
    afterAnnotations
  } = t0;
  const containerRef = useRef(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isSliderDragging, setIsSliderDragging] = useState(false);
  const [isTouchPanning, setIsTouchPanning] = useState(false);
  const [isPinching, setIsPinching] = useState(false);
  const lastTouchRef = useRef(null);
  const lastPinchDistanceRef = useRef(null);
  const initialPinchZoomRef = useRef(1);
  let t1;
  if ($[0] !== onPositionChange) {
    t1 = clientX => {
      if (!containerRef.current) {
        return;
      }
      const rect = containerRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, x / rect.width * 100));
      setSliderPosition(percentage);
      onPositionChange?.(percentage);
    };
    $[0] = onPositionChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSliderMove = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsSliderDragging(true);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleSliderMouseDown = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIsSliderDragging(false);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleSliderMouseUp = t3;
  let t4;
  if ($[4] !== isSliderDragging || $[5] !== syncZoom) {
    t4 = e => {
      if (isSliderDragging) {
        return;
      }
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        lastTouchRef.current = {
          x: touch.clientX,
          y: touch.clientY
        };
        setIsTouchPanning(true);
        setIsPinching(false);
      } else {
        if (e.touches.length === 2) {
          e.preventDefault();
          const touch1 = e.touches[0];
          const touch2 = e.touches[1];
          const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
          lastPinchDistanceRef.current = distance;
          initialPinchZoomRef.current = syncZoom ?? 1;
          setIsPinching(true);
          setIsTouchPanning(false);
        }
      }
    };
    $[4] = isSliderDragging;
    $[5] = syncZoom;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleTouchStart = t4;
  let t5;
  if ($[7] !== isPinching || $[8] !== isTouchPanning || $[9] !== onSyncPanChange || $[10] !== onSyncZoomChange || $[11] !== syncPan) {
    t5 = e_0 => {
      if (!containerRef.current) {
        return;
      }
      if (e_0.touches.length === 1 && isTouchPanning && lastTouchRef.current && onSyncPanChange) {
        const touch_0 = e_0.touches[0];
        const rect_0 = containerRef.current.getBoundingClientRect();
        const deltaX = (touch_0.clientX - lastTouchRef.current.x) / rect_0.width;
        const deltaY = (touch_0.clientY - lastTouchRef.current.y) / rect_0.height;
        const currentPan = syncPan ?? {
          x: 0,
          y: 0
        };
        onSyncPanChange({
          x: Math.max(-1, Math.min(1, currentPan.x + deltaX)),
          y: Math.max(-1, Math.min(1, currentPan.y + deltaY))
        });
        lastTouchRef.current = {
          x: touch_0.clientX,
          y: touch_0.clientY
        };
      } else {
        if (e_0.touches.length === 2 && isPinching && lastPinchDistanceRef.current !== null && onSyncZoomChange) {
          e_0.preventDefault();
          const touch1_0 = e_0.touches[0];
          const touch2_0 = e_0.touches[1];
          const currentDistance = Math.hypot(touch2_0.clientX - touch1_0.clientX, touch2_0.clientY - touch1_0.clientY);
          const scale = currentDistance / lastPinchDistanceRef.current;
          const newZoom = Math.max(0.5, Math.min(5, initialPinchZoomRef.current * scale));
          onSyncZoomChange(newZoom);
        }
      }
    };
    $[7] = isPinching;
    $[8] = isTouchPanning;
    $[9] = onSyncPanChange;
    $[10] = onSyncZoomChange;
    $[11] = syncPan;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleTouchMove = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsTouchPanning(false);
      setIsPinching(false);
      lastTouchRef.current = null;
      lastPinchDistanceRef.current = null;
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleTouchEnd = t6;
  let t7;
  if ($[14] !== onSyncZoomChange || $[15] !== syncZoom) {
    t7 = e_1 => {
      if (!onSyncZoomChange) {
        return;
      }
      e_1.preventDefault();
      const delta = -e_1.deltaY * 0.001;
      const currentZoom = syncZoom ?? 1;
      const newZoom_0 = Math.max(0.5, Math.min(5, currentZoom + delta));
      onSyncZoomChange(newZoom_0);
    };
    $[14] = onSyncZoomChange;
    $[15] = syncZoom;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleWheel = t7;
  let t8;
  let t9;
  if ($[17] !== handleSliderMove || $[18] !== isSliderDragging) {
    t8 = () => {
      const handleMouseMove = e_2 => {
        if (isSliderDragging) {
          handleSliderMove(e_2.clientX);
        }
      };
      const handleTouchMoveSlider = e_3 => {
        if (isSliderDragging && e_3.touches[0]) {
          handleSliderMove(e_3.touches[0].clientX);
        }
      };
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleSliderMouseUp);
      window.addEventListener("touchmove", handleTouchMoveSlider);
      window.addEventListener("touchend", handleSliderMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleSliderMouseUp);
        window.removeEventListener("touchmove", handleTouchMoveSlider);
        window.removeEventListener("touchend", handleSliderMouseUp);
      };
    };
    t9 = [isSliderDragging, handleSliderMove];
    $[17] = handleSliderMove;
    $[18] = isSliderDragging;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  if ($[21] !== syncPan || $[22] !== syncZoom) {
    t10 = baseTransform => {
      const baseStyle = baseTransform ? getTransformStyle(baseTransform) : {};
      const currentSyncZoom = syncZoom ?? 1;
      const currentSyncPan = syncPan ?? {
        x: 0,
        y: 0
      };
      if (currentSyncZoom === 1 && currentSyncPan.x === 0 && currentSyncPan.y === 0) {
        return baseStyle;
      }
      const baseTransformStr = baseStyle.transform as string || "";
      const syncTransform = `scale(${currentSyncZoom}) translate(${currentSyncPan.x * 100}%, ${currentSyncPan.y * 100}%)`;
      return {
        ...baseStyle,
        transform: baseTransformStr ? `${syncTransform} ${baseTransformStr}` : syncTransform,
        transformOrigin: "center center"
      };
    };
    $[21] = syncPan;
    $[22] = syncZoom;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const getComposedStyle = t10;
  let t11;
  if ($[24] !== beforeTransform || $[25] !== getComposedStyle) {
    t11 = getComposedStyle(beforeTransform);
    $[24] = beforeTransform;
    $[25] = getComposedStyle;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const beforeStyle = t11;
  let t12;
  if ($[27] !== afterTransform || $[28] !== getComposedStyle) {
    t12 = getComposedStyle(afterTransform);
    $[27] = afterTransform;
    $[28] = getComposedStyle;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const afterStyle = t12;
  const t13 = compareName || "After";
  let t14;
  if ($[30] !== afterStyle || $[31] !== compareUrl || $[32] !== t13) {
    t14 = <Image src={compareUrl} alt={t13} fill={true} className="object-contain" style={afterStyle} unoptimized={true} priority={true} sizes="100vw" onLoad={_temp3} />;
    $[30] = afterStyle;
    $[31] = compareUrl;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== afterAnnotations || $[35] !== afterStyle || $[36] !== compareUrl) {
    t15 = <ComparisonAnnotationOverlay annotations={afterAnnotations} imageUrl={compareUrl} style={afterStyle} />;
    $[34] = afterAnnotations;
    $[35] = afterStyle;
    $[36] = compareUrl;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15) {
    t16 = <div className="absolute inset-0 overflow-hidden">{t14}{t15}</div>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const t17 = `inset(0 ${100 - sliderPosition}% 0 0)`;
  let t18;
  if ($[41] !== t17) {
    t18 = {
      clipPath: t17
    };
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const t19 = sourceName || "Before";
  let t20;
  if ($[43] !== beforeStyle || $[44] !== sourceUrl || $[45] !== t19) {
    t20 = <Image src={sourceUrl} alt={t19} fill={true} className="object-contain" style={beforeStyle} unoptimized={true} priority={true} sizes="100vw" onLoad={_temp4} />;
    $[43] = beforeStyle;
    $[44] = sourceUrl;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== beforeAnnotations || $[48] !== beforeStyle || $[49] !== sourceUrl) {
    t21 = <ComparisonAnnotationOverlay annotations={beforeAnnotations} imageUrl={sourceUrl} style={beforeStyle} />;
    $[47] = beforeAnnotations;
    $[48] = beforeStyle;
    $[49] = sourceUrl;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t18 || $[52] !== t20 || $[53] !== t21) {
    t22 = <div className="absolute inset-0 overflow-hidden" style={t18}>{t20}{t21}</div>;
    $[51] = t18;
    $[52] = t20;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  const t23 = `${sliderPosition}%`;
  let t24;
  if ($[55] !== t23) {
    t24 = {
      left: t23,
      transform: "translateX(-50%)"
    };
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = e_4 => {
      e_4.stopPropagation();
      handleSliderMouseDown();
    };
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-black/60 backdrop-blur-sm"><div className="flex gap-0.5"><div className="h-4 w-0.5 rounded-full bg-background" /><div className="h-4 w-0.5 rounded-full bg-background" /></div></div>;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t24) {
    t27 = <div className="absolute bottom-0 top-0 z-10 w-1 cursor-ew-resize bg-background shadow-lg" style={t24} onMouseDown={handleSliderMouseDown} onTouchStart={t25}>{t26}</div>;
    $[59] = t24;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div className="absolute left-0 top-0"><span className="bg-black/70 px-1 text-tiny text-white">Before</span></div>;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="absolute right-0 top-0"><span className="bg-black/70 px-1 text-tiny text-white">After</span></div>;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== syncZoom) {
    t30 = syncZoom && syncZoom !== 1 && <div className="absolute bottom-2 left-2 z-10"><span className="rounded bg-black/70 px-2 py-1 text-caption text-white">{Math.round(syncZoom * 100)}%</span></div>;
    $[63] = syncZoom;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== handleTouchMove || $[66] !== handleTouchStart || $[67] !== handleWheel || $[68] !== t16 || $[69] !== t22 || $[70] !== t27 || $[71] !== t30) {
    t31 = <div ref={containerRef} className="relative h-full w-full select-none overflow-hidden rounded-lg bg-black touch-none" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onWheel={handleWheel}>{t16}{t22}{t27}{t28}{t29}{t30}</div>;
    $[65] = handleTouchMove;
    $[66] = handleTouchStart;
    $[67] = handleWheel;
    $[68] = t16;
    $[69] = t22;
    $[70] = t27;
    $[71] = t30;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  return t31;
};

// Image selector thumbnail
const ImageThumbnail = t0 => {
  const $ = _c(19);
  const {
    attachment,
    isSelected,
    onClick
  } = t0;
  const t1 = isSelected ? "border-primary ring-2 ring-primary/30" : "border-transparent hover:border-border";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("relative aspect-square cursor-pointer overflow-hidden rounded-lg border-2 transition-all", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = attachment.thumbnailSignedUrl || attachment.signedUrl || "";
  const t4 = attachment.name || attachment.originalFilename || "";
  let t5;
  if ($[2] !== attachment.thumbnailSignedUrl) {
    t5 = t6 => {
      const {
        usedFallback
      } = t6;
      return reportScribeAttachmentLoaded(!!attachment.thumbnailSignedUrl && !usedFallback);
    };
    $[2] = attachment.thumbnailSignedUrl;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== attachment.signedUrl || $[5] !== t3 || $[6] !== t4 || $[7] !== t5) {
    t6 = <LazyImage src={t3} fallbackSrc={attachment.signedUrl} alt={t4} width={200} height={200} onLoad={t5} />;
    $[4] = attachment.signedUrl;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isSelected) {
    t7 = isSelected && <div className="absolute inset-0 flex items-center justify-center bg-primary/20"><Badge variant="default">✓</Badge></div>;
    $[9] = isSelected;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== attachment.patient) {
    t8 = attachment.patient && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5"><p className="truncate text-tiny font-medium text-white">{formatName(attachment.patient.firstName, attachment.patient.lastName)}</p></div>;
    $[11] = attachment.patient;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== onClick || $[14] !== t2 || $[15] !== t6 || $[16] !== t7 || $[17] !== t8) {
    t9 = <div onClick={onClick} className={t2}>{t6}{t7}{t8}</div>;
    $[13] = onClick;
    $[14] = t2;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
};

// Transformable image component with pan, zoom, rotation, and crop
interface TransformableImageProps {
  imageUrl: string;
  imageName?: string;
  label: string;
  transform: ImageTransform;
  onTransformChange: (transform: ImageTransform) => void;
  onSelect?: () => void;
  cropMode: boolean;
  pendingCrop: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  onPendingCropChange: (crop: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null) => void;
  annotations?: Annotation[];
}
export const TransformableImage = t0 => {
  const $ = _c(81);
  const {
    imageUrl,
    imageName,
    label,
    transform,
    onTransformChange,
    onSelect,
    cropMode,
    pendingCrop,
    onPendingCropChange,
    annotations
  } = t0;
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 0,
      y: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [dragStart, setDragStart] = useState(t1);
  const [isCropDragging, setIsCropDragging] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      x: 0,
      y: 0
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [cropStart, setCropStart] = useState(t2);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [isGesturing, setIsGesturing] = useState(false);
  const lastTouchRef = useRef(null);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      distance: 0,
      zoom: 1
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const gestureStartRef = useRef(t3);
  const getTouchDistance = _temp5;
  let t4;
  if ($[3] !== cropMode || $[4] !== onSelect || $[5] !== transform.panX || $[6] !== transform.panY) {
    t4 = e => {
      if (cropMode || !containerRef.current) {
        return;
      }
      onSelect?.();
      e.preventDefault();
      const rect = containerRef.current.getBoundingClientRect();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - transform.panX * rect.width,
        y: e.clientY - transform.panY * rect.height
      });
    };
    $[3] = cropMode;
    $[4] = onSelect;
    $[5] = transform.panX;
    $[6] = transform.panY;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleMouseDown = t4;
  let t5;
  if ($[8] !== dragStart || $[9] !== isDragging || $[10] !== onTransformChange || $[11] !== transform) {
    t5 = e_0 => {
      if (!isDragging || !containerRef.current) {
        return;
      }
      const rect_0 = containerRef.current.getBoundingClientRect();
      const newPanX = (e_0.clientX - dragStart.x) / rect_0.width;
      const newPanY = (e_0.clientY - dragStart.y) / rect_0.height;
      onTransformChange({
        ...transform,
        panX: Math.max(-1, Math.min(1, newPanX)),
        panY: Math.max(-1, Math.min(1, newPanY))
      });
    };
    $[8] = dragStart;
    $[9] = isDragging;
    $[10] = onTransformChange;
    $[11] = transform;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleMouseMove = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsDragging(false);
      setIsCropDragging(false);
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleMouseUp = t6;
  let t7;
  if ($[14] !== cropMode || $[15] !== onPendingCropChange) {
    t7 = e_1 => {
      if (!cropMode || !containerRef.current) {
        return;
      }
      e_1.preventDefault();
      e_1.stopPropagation();
      const rect_1 = containerRef.current.getBoundingClientRect();
      const x = (e_1.clientX - rect_1.left) / rect_1.width;
      const y = (e_1.clientY - rect_1.top) / rect_1.height;
      setIsCropDragging(true);
      setCropStart({
        x,
        y
      });
      onPendingCropChange({
        x,
        y,
        width: 0,
        height: 0
      });
    };
    $[14] = cropMode;
    $[15] = onPendingCropChange;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleCropMouseDown = t7;
  let t8;
  if ($[17] !== cropStart || $[18] !== isCropDragging || $[19] !== onPendingCropChange) {
    t8 = e_2 => {
      if (!isCropDragging || !containerRef.current) {
        return;
      }
      const rect_2 = containerRef.current.getBoundingClientRect();
      const currentX = Math.max(0, Math.min(1, (e_2.clientX - rect_2.left) / rect_2.width));
      const currentY = Math.max(0, Math.min(1, (e_2.clientY - rect_2.top) / rect_2.height));
      const cropX = Math.min(cropStart.x, currentX);
      const cropY = Math.min(cropStart.y, currentY);
      const cropWidth = Math.abs(currentX - cropStart.x);
      const cropHeight = Math.abs(currentY - cropStart.y);
      onPendingCropChange({
        x: cropX,
        y: cropY,
        width: cropWidth,
        height: cropHeight
      });
    };
    $[17] = cropStart;
    $[18] = isCropDragging;
    $[19] = onPendingCropChange;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleCropMouseMove = t8;
  let t9;
  if ($[21] !== cropMode || $[22] !== onSelect || $[23] !== transform.zoom) {
    t9 = e_3 => {
      if (cropMode) {
        return;
      }
      onSelect?.();
      if (e_3.touches.length === 1) {
        const touch = e_3.touches[0];
        lastTouchRef.current = {
          x: touch.clientX,
          y: touch.clientY
        };
        setIsTouchDragging(true);
        setIsGesturing(false);
      } else {
        if (e_3.touches.length === 2) {
          e_3.preventDefault();
          gestureStartRef.current = {
            distance: getTouchDistance(e_3.touches),
            zoom: transform.zoom
          };
          setIsGesturing(true);
          setIsTouchDragging(false);
        }
      }
    };
    $[21] = cropMode;
    $[22] = onSelect;
    $[23] = transform.zoom;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleTouchStart = t9;
  let t10;
  if ($[25] !== cropMode || $[26] !== isGesturing || $[27] !== isTouchDragging || $[28] !== onTransformChange || $[29] !== transform) {
    t10 = e_4 => {
      if (cropMode || !containerRef.current) {
        return;
      }
      if (e_4.touches.length === 1 && isTouchDragging && lastTouchRef.current) {
        const touch_0 = e_4.touches[0];
        const rect_3 = containerRef.current.getBoundingClientRect();
        const deltaX = (touch_0.clientX - lastTouchRef.current.x) / rect_3.width;
        const deltaY = (touch_0.clientY - lastTouchRef.current.y) / rect_3.height;
        onTransformChange({
          ...transform,
          panX: Math.max(-1, Math.min(1, transform.panX + deltaX)),
          panY: Math.max(-1, Math.min(1, transform.panY + deltaY))
        });
        lastTouchRef.current = {
          x: touch_0.clientX,
          y: touch_0.clientY
        };
      } else {
        if (e_4.touches.length === 2 && isGesturing) {
          e_4.preventDefault();
          const start = gestureStartRef.current;
          const currentDistance = getTouchDistance(e_4.touches);
          let newZoom = transform.zoom;
          if (start.distance > 0) {
            const scale = currentDistance / start.distance;
            newZoom = Math.max(0.5, Math.min(3, start.zoom * scale));
          }
          onTransformChange({
            ...transform,
            zoom: newZoom
          });
        }
      }
    };
    $[25] = cropMode;
    $[26] = isGesturing;
    $[27] = isTouchDragging;
    $[28] = onTransformChange;
    $[29] = transform;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const handleTouchMove = t10;
  let t11;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setIsTouchDragging(false);
      setIsGesturing(false);
      lastTouchRef.current = null;
    };
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const handleTouchEnd = t11;
  let t12;
  if ($[32] !== cropMode || $[33] !== onSelect || $[34] !== onTransformChange || $[35] !== transform) {
    t12 = e_5 => {
      if (cropMode) {
        return;
      }
      onSelect?.();
      e_5.preventDefault();
      if (e_5.shiftKey) {
        const currentRotation = transform.rotation ?? 0;
        onTransformChange({
          ...transform,
          rotation: currentRotation - e_5.deltaY * 0.1
        });
        return;
      }
      const delta = -e_5.deltaY * 0.001;
      const newZoom_0 = Math.max(0.5, Math.min(3, transform.zoom + delta));
      onTransformChange({
        ...transform,
        zoom: newZoom_0
      });
    };
    $[32] = cropMode;
    $[33] = onSelect;
    $[34] = onTransformChange;
    $[35] = transform;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const handleWheel = t12;
  let t13;
  let t14;
  if ($[37] !== handleMouseMove || $[38] !== isDragging) {
    t13 = () => {
      if (isDragging) {
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
          window.removeEventListener("mousemove", handleMouseMove);
          window.removeEventListener("mouseup", handleMouseUp);
        };
      }
    };
    t14 = [isDragging, handleMouseMove, handleMouseUp];
    $[37] = handleMouseMove;
    $[38] = isDragging;
    $[39] = t13;
    $[40] = t14;
  } else {
    t13 = $[39];
    t14 = $[40];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[41] !== handleCropMouseMove || $[42] !== isCropDragging) {
    t15 = () => {
      if (isCropDragging) {
        window.addEventListener("mousemove", handleCropMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
          window.removeEventListener("mousemove", handleCropMouseMove);
          window.removeEventListener("mouseup", handleMouseUp);
        };
      }
    };
    t16 = [isCropDragging, handleCropMouseMove, handleMouseUp];
    $[41] = handleCropMouseMove;
    $[42] = isCropDragging;
    $[43] = t15;
    $[44] = t16;
  } else {
    t15 = $[43];
    t16 = $[44];
  }
  useEffect(t15, t16);
  const hasPendingCrop = pendingCrop != null && pendingCrop.width > 0.01 && pendingCrop.height > 0.01;
  const displayCrop = cropMode && pendingCrop ? pendingCrop : null;
  const mirrorScale = transform.mirror ? -1 : 1;
  const isLiveGesture = isDragging || isTouchDragging || isGesturing;
  let t17;
  if ($[45] !== cropMode || $[46] !== isLiveGesture || $[47] !== mirrorScale || $[48] !== transform) {
    t17 = cropMode ? {
      transform: `scaleX(${mirrorScale}) scale(${transform.zoom}) translate(${transform.panX * 100}%, ${transform.panY * 100}%)`,
      transformOrigin: "center center",
      transition: isLiveGesture ? "none" : "transform 0.1s ease-out"
    } : {
      ...getTransformStyle(transform),
      transition: isLiveGesture ? "none" : "transform 0.1s ease-out"
    };
    $[45] = cropMode;
    $[46] = isLiveGesture;
    $[47] = mirrorScale;
    $[48] = transform;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  const imageStyle = t17;
  const t18 = !cropMode && "cursor-move";
  const t19 = cropMode && "cursor-crosshair";
  let t20;
  if ($[50] !== t18 || $[51] !== t19) {
    t20 = cx("relative h-full w-full overflow-hidden rounded-lg bg-black touch-none", t18, t19);
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  const t21 = cropMode ? handleCropMouseDown : handleMouseDown;
  const t22 = imageName || label;
  let t23;
  if ($[53] !== imageUrl || $[54] !== t22) {
    t23 = <Image src={imageUrl} alt={t22} fill={true} className="object-contain pointer-events-none" unoptimized={true} draggable={false} priority={true} sizes="(max-width: 768px) 100vw, 50vw" onLoad={_temp6} />;
    $[53] = imageUrl;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== annotations || $[57] !== imageUrl) {
    t24 = <ComparisonAnnotationOverlay annotations={annotations} imageUrl={imageUrl} />;
    $[56] = annotations;
    $[57] = imageUrl;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== imageStyle || $[60] !== t23 || $[61] !== t24) {
    t25 = <div className="absolute inset-0" style={imageStyle}>{t23}{t24}</div>;
    $[59] = imageStyle;
    $[60] = t23;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== cropMode || $[64] !== displayCrop || $[65] !== hasPendingCrop) {
    t26 = cropMode && <div className="absolute inset-0 pointer-events-none">{displayCrop && hasPendingCrop && <><div className="absolute left-0 right-0 top-0 bg-black/60" style={{
          height: `${displayCrop.y * 100}%`
        }} /><div className="absolute bottom-0 left-0 right-0 bg-black/60" style={{
          height: `${(1 - displayCrop.y - displayCrop.height) * 100}%`
        }} /><div className="absolute left-0 bg-black/60" style={{
          top: `${displayCrop.y * 100}%`,
          height: `${displayCrop.height * 100}%`,
          width: `${displayCrop.x * 100}%`
        }} /><div className="absolute right-0 bg-black/60" style={{
          top: `${displayCrop.y * 100}%`,
          height: `${displayCrop.height * 100}%`,
          width: `${(1 - displayCrop.x - displayCrop.width) * 100}%`
        }} /><div className="absolute border-2 border-white border-dashed" style={{
          left: `${displayCrop.x * 100}%`,
          top: `${displayCrop.y * 100}%`,
          width: `${displayCrop.width * 100}%`,
          height: `${displayCrop.height * 100}%`
        }} /></>}{!hasPendingCrop && <div className="absolute inset-0 flex items-center justify-center"><p className="rounded bg-black/60 px-3 py-1.5 text-body text-white">Click and drag to select crop area</p></div>}</div>;
    $[63] = cropMode;
    $[64] = displayCrop;
    $[65] = hasPendingCrop;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== label) {
    t27 = <div className="absolute left-0 top-0 pointer-events-none"><span className="bg-black/70 px-1 text-tiny text-white">{label}</span></div>;
    $[67] = label;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== transform) {
    t28 = hasTransformChanges(transform) && <div className="absolute right-3 top-3"><Badge variant="dark" className="text-caption">Adjusted</Badge></div>;
    $[69] = transform;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== handleTouchMove || $[72] !== handleTouchStart || $[73] !== handleWheel || $[74] !== t20 || $[75] !== t21 || $[76] !== t25 || $[77] !== t26 || $[78] !== t27 || $[79] !== t28) {
    t29 = <div ref={containerRef} className={t20} onMouseDown={t21} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onWheel={handleWheel}>{t25}{t26}{t27}{t28}</div>;
    $[71] = handleTouchMove;
    $[72] = handleTouchStart;
    $[73] = handleWheel;
    $[74] = t20;
    $[75] = t21;
    $[76] = t25;
    $[77] = t26;
    $[78] = t27;
    $[79] = t28;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  return t29;
};

// Transform controls toolbar
interface TransformControlsProps {
  transform: ImageTransform;
  onTransformChange: (transform: ImageTransform) => void;
  cropMode: boolean;
  onCropModeChange: (mode: boolean) => void;
  onReset: () => void;
  label: string;
  pendingCrop: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  onPendingCropChange: (crop: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null) => void;
  size?: "sm" | "default";
}
const TransformControls = t0 => {
  const $ = _c(55);
  const {
    transform,
    onTransformChange,
    cropMode,
    onCropModeChange,
    onReset,
    label,
    pendingCrop,
    onPendingCropChange,
    size: t1
  } = t0;
  const size = t1 === undefined ? "default" : t1;
  let t2;
  if ($[0] !== onCropModeChange || $[1] !== onPendingCropChange || $[2] !== onTransformChange || $[3] !== transform) {
    t2 = () => {
      onTransformChange({
        ...transform,
        cropX: null,
        cropY: null,
        cropWidth: null,
        cropHeight: null
      });
      onPendingCropChange(null);
      onCropModeChange(false);
    };
    $[0] = onCropModeChange;
    $[1] = onPendingCropChange;
    $[2] = onTransformChange;
    $[3] = transform;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleClearCrop = t2;
  let t3;
  if ($[5] !== onCropModeChange || $[6] !== onPendingCropChange || $[7] !== onTransformChange || $[8] !== pendingCrop || $[9] !== transform) {
    t3 = () => {
      if (pendingCrop && pendingCrop.width > 0.01 && pendingCrop.height > 0.01) {
        onTransformChange({
          ...transform,
          cropX: pendingCrop.x,
          cropY: pendingCrop.y,
          cropWidth: pendingCrop.width,
          cropHeight: pendingCrop.height
        });
      }
      onPendingCropChange(null);
      onCropModeChange(false);
    };
    $[5] = onCropModeChange;
    $[6] = onPendingCropChange;
    $[7] = onTransformChange;
    $[8] = pendingCrop;
    $[9] = transform;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleApplyCrop = t3;
  let t4;
  if ($[11] !== onCropModeChange || $[12] !== onPendingCropChange) {
    t4 = () => {
      onPendingCropChange(null);
      onCropModeChange(false);
    };
    $[11] = onCropModeChange;
    $[12] = onPendingCropChange;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleCancelCrop = t4;
  let t5;
  if ($[14] !== transform) {
    t5 = hasCropApplied(transform);
    $[14] = transform;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const hasAppliedCrop = t5;
  const hasPendingCrop = pendingCrop != null && pendingCrop.width > 0.01 && pendingCrop.height > 0.01;
  let t6;
  if ($[16] !== label) {
    t6 = <span className="hidden text-caption-strong text-muted-foreground sm:inline">{label}:</span>;
    $[16] = label;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const t7 = transform.zoom;
  let t8;
  if ($[18] !== onTransformChange || $[19] !== transform) {
    t8 = zoom => onTransformChange({
      ...transform,
      zoom
    });
    $[18] = onTransformChange;
    $[19] = transform;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = transform.rotation ?? 0;
  let t10;
  if ($[21] !== onTransformChange || $[22] !== transform) {
    t10 = rotation => onTransformChange({
      ...transform,
      rotation
    });
    $[21] = onTransformChange;
    $[22] = transform;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = transform.mirror;
  let t12;
  if ($[24] !== onTransformChange || $[25] !== transform) {
    t12 = mirror => onTransformChange({
      ...transform,
      mirror
    });
    $[24] = onTransformChange;
    $[25] = transform;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== transform) {
    t13 = hasTransformChanges(transform);
    $[27] = transform;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== cropMode || $[30] !== onCropModeChange || $[31] !== onReset || $[32] !== size || $[33] !== t10 || $[34] !== t12 || $[35] !== t13 || $[36] !== t8 || $[37] !== t9 || $[38] !== transform.mirror || $[39] !== transform.zoom) {
    t14 = <ImageTransformControls zoom={t7} onZoomChange={t8} rotation={t9} onRotationChange={t10} cropMode={cropMode} onCropModeChange={onCropModeChange} mirror={t11} onMirrorChange={t12} showMirror={true} onReset={onReset} showReset={true} hasChanges={t13} size={size} />;
    $[29] = cropMode;
    $[30] = onCropModeChange;
    $[31] = onReset;
    $[32] = size;
    $[33] = t10;
    $[34] = t12;
    $[35] = t13;
    $[36] = t8;
    $[37] = t9;
    $[38] = transform.mirror;
    $[39] = transform.zoom;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== cropMode || $[42] !== handleApplyCrop || $[43] !== handleCancelCrop || $[44] !== hasPendingCrop) {
    t15 = cropMode && hasPendingCrop && <><Button variant="primary" size="sm" onClick={handleApplyCrop} className="h-7 gap-1 px-2" title="Apply crop"><span className="text-caption">Apply Crop</span></Button><Button variant="secondary" size="sm" onClick={handleCancelCrop} className="h-7 gap-1 px-2" title="Cancel crop"><span className="text-caption">Cancel</span></Button></>;
    $[41] = cropMode;
    $[42] = handleApplyCrop;
    $[43] = handleCancelCrop;
    $[44] = hasPendingCrop;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== cropMode || $[47] !== handleClearCrop || $[48] !== hasAppliedCrop) {
    t16 = !cropMode && hasAppliedCrop && <Button variant="ghost" size="sm" onClick={handleClearCrop} className="h-7 gap-1 px-2 text-status-error-text" title="Clear crop"><FiX className="h-3.5 w-3.5" /><span className="text-caption">Clear Crop</span></Button>;
    $[46] = cropMode;
    $[47] = handleClearCrop;
    $[48] = hasAppliedCrop;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== t14 || $[51] !== t15 || $[52] !== t16 || $[53] !== t6) {
    t17 = <div className="flex flex-wrap items-center gap-1.5 rounded-lg bg-background-secondary p-1.5 sm:gap-2 sm:p-2">{t6}{t14}{t15}{t16}</div>;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
    $[53] = t6;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  return t17;
};
export default function ImageCompareModal({
  open,
  onOpenChange,
  sourceImage,
  availableImages,
  patientId: propPatientId,
  hasMoreImages = false,
  isLoadingMoreImages = false,
  onLoadMoreImages
}: ImageCompareModalProps) {
  const [compareMode, setCompareMode] = useState<CompareMode>("side-by-side");
  const [selectedCompareImage, setSelectedCompareImage] = useState<NoteAttachment | null>(null);
  const [selectedSourceImage, setSelectedSourceImage] = useState<NoteAttachment | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectorMode, setSelectorMode] = useState<"before" | "after">("after");
  const [isSaving, setIsSaving] = useState(false);
  const [patientSelectorOpen, setPatientSelectorOpen] = useState(false);
  const [selectedPatientForSave, setSelectedPatientForSave] = useState<PatientSummary | undefined>();
  const sliderPositionRef = useRef<number>(50);

  // Transform state for image alignment
  const [beforeTransform, setBeforeTransform] = useState<ImageTransform>({
    ...DEFAULT_TRANSFORM
  });
  const [afterTransform, setAfterTransform] = useState<ImageTransform>({
    ...DEFAULT_TRANSFORM
  });
  const [selectedImage, setSelectedImage] = useState<"before" | "after">("before");
  const [beforeCropMode, setBeforeCropMode] = useState(false);
  const [afterCropMode, setAfterCropMode] = useState(false);
  // Pending crop state (before user confirms)
  const [beforePendingCrop, setBeforePendingCrop] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [afterPendingCrop, setAfterPendingCrop] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  // Sync zoom/pan state for overlay/slider mode
  const [syncZoom, setSyncZoom] = useState(1);
  const [syncPan, setSyncPan] = useState({
    x: 0,
    y: 0
  });

  // Studio Mode: one backdrop for both photos, and face auto-align.
  const {
    currentUser
  } = useUser();
  const canEditStudio = hasPermission(currentUser, PERMISSIONS.PATIENTS_NOTES_WRITE);
  const [compareBackdrop, setCompareBackdrop] = useState<StudioBackdrop>("NONE");
  const [isPreparingStudio, setIsPreparingStudio] = useState(false);
  const [isAligning, setIsAligning] = useState(false);
  const displayAreaRef = useRef<HTMLDivElement>(null);
  const {
    data: studioSettings
  } = usePhotoStudioSettings(open);

  // Use selected source image or fall back to prop
  const currentSourceImage = useMemo(() => {
    return selectedSourceImage || sourceImage;
  }, [selectedSourceImage, sourceImage]);

  // Get patientId from prop, source image, current source image, selected patient, or available images
  const patientId = useMemo(() => {
    return selectedPatientForSave?.id || propPatientId || currentSourceImage?.patient?.id || sourceImage?.patient?.id || selectedCompareImage?.patient?.id || availableImages.find(img => img.patient?.id)?.patient?.id || "";
  }, [selectedPatientForSave, propPatientId, currentSourceImage, sourceImage, selectedCompareImage, availableImages]);

  // Filter out the current source image from available images
  const attachments = useMemo(() => {
    return availableImages.filter(a => a.id !== currentSourceImage?.id);
  }, [availableImages, currentSourceImage?.id]);

  // Filter by search term
  const filteredAttachments = useMemo(() => {
    if (!searchTerm.trim()) return attachments;
    const term = searchTerm.toLowerCase();
    return attachments.filter(a_0 => {
      const name = (a_0.name || a_0.originalFilename || "").toLowerCase();
      const patientName = a_0.patient ? `${a_0.patient.firstName} ${a_0.patient.lastName}`.toLowerCase() : "";
      return name.includes(term) || patientName.includes(term);
    });
  }, [attachments, searchTerm]);
  const canLoadMore = hasMoreImages && !!onLoadMoreImages;
  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);
  const setLoadMoreObserver = useCallback((node: HTMLDivElement | null) => {
    if (isLoadingMoreImages) return;
    if (loadMoreObserverRef.current) loadMoreObserverRef.current.disconnect();
    loadMoreObserverRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && canLoadMore) {
        onLoadMoreImages?.();
      }
    });
    if (node) loadMoreObserverRef.current.observe(node);
  }, [canLoadMore, isLoadingMoreImages, onLoadMoreImages]);
  useEffect(() => {
    return () => loadMoreObserverRef.current?.disconnect();
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedCompareImage(null);
      setSelectedSourceImage(null);
      setSearchTerm("");
      setSelectorMode("after");
      // Reset transforms
      setBeforeTransform({
        ...DEFAULT_TRANSFORM
      });
      setAfterTransform({
        ...DEFAULT_TRANSFORM
      });
      setSelectedImage("before");
      setBeforeCropMode(false);
      setAfterCropMode(false);
      setBeforePendingCrop(null);
      setAfterPendingCrop(null);
      // Reset sync zoom/pan for overlay mode
      setSyncZoom(1);
      setSyncPan({
        x: 0,
        y: 0
      });
      setCompareBackdrop("NONE");
    }
  }, [open]);
  const beforeStudioId = currentSourceImage?.attachmentId || currentSourceImage?.id;
  const afterStudioId = selectedCompareImage?.attachmentId || selectedCompareImage?.id;
  const studioActive = open && compareBackdrop !== "NONE";
  const {
    data: beforeRender
  } = useStudioRender(beforeStudioId, {
    backdrop: compareBackdrop,
    size: "FULL"
  }, studioActive && isStudioImagePath(currentSourceImage?.gcsPath));
  const {
    data: afterRender
  } = useStudioRender(afterStudioId, {
    backdrop: compareBackdrop,
    size: "FULL"
  }, studioActive && isStudioImagePath(selectedCompareImage?.gcsPath));
  // Studio versions when a backdrop is picked; a photo without a cutout stays
  // as taken. The patient's own pixels are the same in both.
  const beforeDisplayUrl = (studioActive ? beforeRender?.signedUrl : null) || currentSourceImage?.signedUrl || currentSourceImage?.url || "";
  const afterDisplayUrl = (studioActive ? afterRender?.signedUrl : null) || selectedCompareImage?.signedUrl || selectedCompareImage?.url || "";
  const handleCompareBackdropChange = useCallback(async (backdrop: StudioBackdrop) => {
    setCompareBackdrop(backdrop);
    if (backdrop === "NONE" || !canEditStudio) return;
    const photos = [currentSourceImage, selectedCompareImage].filter((photo): photo is NoteAttachment => !!photo && isStudioImagePath(photo.gcsPath));
    if (photos.length === 0) return;
    setIsPreparingStudio(true);
    try {
      // Photos without a cutout get one made on this device first.
      const results = await Promise.all(photos.map(async photo_0 => {
        const state = await queryClient.fetchQuery(photoStudioQueryOptions.attachment(photo_0.attachmentId || photo_0.id));
        return state.hasMask || (await ensureStudioMask(state));
      }));
      if (results.some(found => !found)) {
        toast({
          title: "No one found in a photo",
          description: "That photo is shown as taken, because Studio needs a person in it to replace the background.",
          variant: "warning"
        });
      }
    } catch (error) {
      console.error("Failed to prepare Studio photos:", error);
    } finally {
      setIsPreparingStudio(false);
    }
  }, [canEditStudio, currentSourceImage, selectedCompareImage]);

  // Line the After photo's face up with the Before photo's: move, scale and
  // rotate only.
  const handleAutoAlign = useCallback(async () => {
    const beforeUrl = currentSourceImage?.signedUrl || currentSourceImage?.url || "";
    const afterUrl = selectedCompareImage?.signedUrl || selectedCompareImage?.url || "";
    const area = displayAreaRef.current;
    if (!beforeUrl || !afterUrl || !area) return;
    setIsAligning(true);
    try {
      const landmarker = await getFaceLandmarker();
      if (!landmarker) throw new Error("Face detection unavailable");
      const [beforeImage, afterImage] = await Promise.all([loadDetectableImage(beforeUrl), loadDetectableImage(afterUrl)]);
      const frame = compareMode === "side-by-side" ? {
        width: (area.clientWidth - SIDE_BY_SIDE_GAP_PX) / 2,
        height: area.clientHeight
      } : {
        width: area.clientWidth,
        height: area.clientHeight
      };
      const alignment = alignPair(detectPose(landmarker, beforeImage, false), {
        width: beforeImage.naturalWidth,
        height: beforeImage.naturalHeight
      }, detectPose(landmarker, afterImage, false), {
        width: afterImage.naturalWidth,
        height: afterImage.naturalHeight
      }, frame, "contain");
      if (!alignment) {
        toast({
          title: "Couldn't line up the faces",
          description: "Auto-align needs a clear, front-facing face in both photos. You can still line them up by hand.",
          variant: "warning"
        });
        return;
      }
      setBeforeTransform({
        ...DEFAULT_TRANSFORM
      });
      setAfterTransform(alignmentToTransform(alignment, frame));
      setSyncZoom(1);
      setSyncPan({
        x: 0,
        y: 0
      });
      setBeforeCropMode(false);
      setAfterCropMode(false);
      setBeforePendingCrop(null);
      setAfterPendingCrop(null);
    } catch (error_0) {
      console.error("Auto-align failed:", error_0);
      toast({
        title: "Auto-align unavailable",
        description: "Face detection could not run on this device.",
        variant: "error"
      });
    } finally {
      setIsAligning(false);
    }
  }, [compareMode, currentSourceImage, selectedCompareImage]);

  // Update slider position ref when slider moves (for download)
  const handleSliderPositionChange = useCallback((position: number) => {
    sliderPositionRef.current = position;
  }, []);
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedCompareImage(null);
      setSelectedSourceImage(null);
      setSearchTerm("");
    }
    onOpenChange(newOpen);
  };

  // Helper to get attachment name
  const getAttachmentName = useCallback((attachment: NoteAttachment | null, fallback: string) => attachment?.name || attachment?.originalFilename || fallback, []);

  // Save comparison image to gallery
  const performSave = useCallback(async (targetPatientId: string) => {
    if (!currentSourceImage || !selectedCompareImage) return;
    setIsSaving(true);
    try {
      const targetNoteId = currentSourceImage?.noteId || sourceImage?.noteId || null;
      const sourceName = getAttachmentName(currentSourceImage, "before");
      const compareName = getAttachmentName(selectedCompareImage, "after");
      const fileName = `comparison-${sourceName}-${compareName}.jpg`;

      // What is on screen, so a Studio backdrop is saved too.
      const beforeImageUrl = beforeDisplayUrl;
      const afterImageUrl = afterDisplayUrl;
      if (!beforeImageUrl || !afterImageUrl) {
        throw new Error("Missing image URLs");
      }

      // In slider mode, merge sync zoom/pan into individual transforms
      // so the saved image reflects what the user sees
      const effectiveBeforeTransform: ImageTransform = compareMode === "slider" ? {
        ...beforeTransform,
        zoom: beforeTransform.zoom * syncZoom,
        panX: beforeTransform.panX + syncPan.x,
        panY: beforeTransform.panY + syncPan.y
      } : beforeTransform;
      const effectiveAfterTransform: ImageTransform = compareMode === "slider" ? {
        ...afterTransform,
        zoom: afterTransform.zoom * syncZoom,
        panX: afterTransform.panX + syncPan.x,
        panY: afterTransform.panY + syncPan.y
      } : afterTransform;

      // Render to canvas on the client and get JPEG blob
      const blob = await renderComparisonToCanvas({
        beforeImageUrl,
        afterImageUrl,
        beforeTransform: effectiveBeforeTransform,
        afterTransform: effectiveAfterTransform,
        mode: compareMode === "slider" ? "overlay" : "side-by-side",
        sliderPosition: sliderPositionRef.current,
        beforeName: sourceName,
        afterName: compareName
      });

      // Upload the rendered JPEG as a file attachment
      const file = new File([blob], fileName, {
        type: "image/jpeg"
      });
      const result = await addNoteAttachment(targetPatientId, file, targetNoteId, fileName);
      if (!result) {
        throw new Error("Failed to upload comparison image");
      }

      // Invalidate all attachment queries to refresh the gallery
      // Use broad key prefix to match queries with any tagIds/dateRange params
      await Promise.all([queryClient.invalidateQueries({
        queryKey: [...noteQueryKeys.all, "attachments-infinite"],
        exact: false
      }), ...(targetNoteId ? [queryClient.invalidateQueries({
        queryKey: noteQueryKeys.attachments({
          noteId: targetNoteId
        })
      })] : [])]);
      toast({
        title: "Saved",
        description: "Comparison image saved to gallery",
        variant: "success"
      });

      // Close modal after successful save
      onOpenChange(false);
    } catch (error_1) {
      console.error("Save failed:", error_1);
      toast({
        title: "Save Failed",
        description: error_1 instanceof Error ? error_1.message : "Unable to save comparison image. Please try again.",
        variant: "error"
      });
    } finally {
      setIsSaving(false);
    }
  }, [currentSourceImage, selectedCompareImage, compareMode, sourceImage, getAttachmentName, onOpenChange, beforeTransform, afterTransform, syncZoom, syncPan, beforeDisplayUrl, afterDisplayUrl]);

  // Handle save button click (checks for patientId first)
  const handleSaveComparison = useCallback(() => {
    if (!currentSourceImage || !selectedCompareImage || isSaving) return;
    if (!patientId) {
      setPatientSelectorOpen(true);
      return;
    }
    performSave(patientId);
  }, [currentSourceImage, selectedCompareImage, isSaving, patientId, performSave]);
  if (!sourceImage) return null;
  const hasCompareImage = !!selectedCompareImage;
  return <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[100dvh] max-w-7xl sm:max-h-[95vh]">
        <DialogHeader className="flex-shrink-0">
          <div className="flex flex-col gap-2 sm:gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center justify-between md:justify-start">
              <DialogTitle className="text-body-lg sm:text-lg">
                Compare
              </DialogTitle>
              <div className="md:hidden">
                <DialogClose asChild>
                  <Button variant="ghost" className="p-1.5">
                    <FiX className="h-5 w-5" />
                  </Button>
                </DialogClose>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {isPreparingStudio && <LuLoader2 className="size-4 animate-spin text-foreground-secondary" aria-label="Preparing Studio photos" />}
                <BackdropSwatches value={compareBackdrop} onChange={backdrop_0 => void handleCompareBackdropChange(backdrop_0)} brandColor={studioSettings?.effectiveBrandColor} exclude={["TRANSPARENT"]} disabled={isPreparingStudio} aria-label="Backdrop for both photos" />
              </div>
              {hasCompareImage && patientId && <MarketingExportButton patientId={patientId} beforeAttachmentId={currentSourceImage?.id} afterAttachmentId={selectedCompareImage?.id} />}
              {hasCompareImage && <Button variant="secondary" onClick={handleSaveComparison} disabled={isSaving} className="gap-1.5" title="Save comparison to gallery" icon={isSaving ? <LuLoader2 className="h-4 w-4 animate-spin" /> : <FiDownload className="h-4 w-4" />}>
                  <span className="hidden sm:inline">Save to Gallery</span>
                  <span className="sm:hidden">Save</span>
                </Button>}
              <Tabs value={compareMode} onValueChange={v => setCompareMode(v as CompareMode)}>
                <TabsList>
                  <TabsTrigger value="side-by-side" className="gap-1.5">
                    <FiColumns className="h-4 w-4" />
                    <span className="hidden sm:inline">Side by Side</span>
                  </TabsTrigger>
                  <TabsTrigger value="slider" className="gap-1.5">
                    <FiLayers className="h-4 w-4" />
                    <span className="hidden sm:inline">Overlay</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="hidden md:block">
                <DialogClose asChild>
                  <Button variant="ghost" className="p-1.5">
                    <FiX className="h-5 w-5" />
                  </Button>
                </DialogClose>
              </div>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="flex min-h-0 flex-1 flex-col gap-3 p-2 pt-0 sm:gap-4 sm:p-4 sm:pt-0 md:flex-row">
          {/* Image comparison area */}
          <div className="flex min-h-[200px] flex-1 flex-col gap-2 sm:min-h-[300px] sm:gap-3 md:min-h-[500px]">
            {/* Image controls - always visible when compare image selected */}
            {hasCompareImage && <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
                {/* Image selector tabs */}
                <Tabs value={selectedImage} onValueChange={v_0 => {
              setSelectedImage(v_0 as "before" | "after");
              // Exit crop mode and clear pending crops when switching
              setBeforeCropMode(false);
              setAfterCropMode(false);
              setBeforePendingCrop(null);
              setAfterPendingCrop(null);
            }}>
                  <TabsList>
                    <TabsTrigger value="before" className="gap-1.5">
                      Before
                      {hasTransformChanges(beforeTransform) && <span className="ml-1 h-2 w-2 rounded-full bg-primary" />}
                    </TabsTrigger>
                    <TabsTrigger value="after" className="gap-1.5">
                      After
                      {hasTransformChanges(afterTransform) && <span className="ml-1 h-2 w-2 rounded-full bg-primary" />}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                <Button variant="secondary" onClick={() => void handleAutoAlign()} disabled={isAligning} isLoading={isAligning} icon={<RiFocus3Line className="h-4 w-4" />} title="Line up the After photo's face with the Before photo" className="shrink-0">
                  Auto-align
                </Button>

                {/* Transform controls for selected image */}
                <div className="-mx-1 overflow-x-auto px-1">
                  {selectedImage === "before" ? <TransformControls transform={beforeTransform} onTransformChange={setBeforeTransform} cropMode={beforeCropMode} onCropModeChange={setBeforeCropMode} onReset={() => setBeforeTransform({
                ...DEFAULT_TRANSFORM
              })} label="Before" pendingCrop={beforePendingCrop} onPendingCropChange={setBeforePendingCrop} /> : <TransformControls transform={afterTransform} onTransformChange={setAfterTransform} cropMode={afterCropMode} onCropModeChange={setAfterCropMode} onReset={() => setAfterTransform({
                ...DEFAULT_TRANSFORM
              })} label="After" pendingCrop={afterPendingCrop} onPendingCropChange={setAfterPendingCrop} />}
                </div>
              </div>}

            {/* Image display area */}
            <div ref={displayAreaRef} className="flex-1 overflow-hidden rounded-lg bg-muted">
              {hasCompareImage ? (() => {
              const sourceUrl = beforeDisplayUrl;
              const compareUrl = afterDisplayUrl;
              if (!sourceUrl || !compareUrl) {
                return <div className="flex h-full items-center justify-center text-foreground-muted">
                        Missing image URL
                      </div>;
              }

              // Slider/overlay mode
              if (compareMode === "slider") {
                return <ImageSliderOverlay sourceUrl={sourceUrl} compareUrl={compareUrl} sourceName={currentSourceImage?.name || currentSourceImage?.originalFilename} compareName={selectedCompareImage.name || selectedCompareImage.originalFilename} onPositionChange={handleSliderPositionChange} beforeTransform={beforeTransform} afterTransform={afterTransform} syncZoom={syncZoom} onSyncZoomChange={setSyncZoom} syncPan={syncPan} onSyncPanChange={setSyncPan} beforeAnnotations={currentSourceImage?.annotations} afterAnnotations={selectedCompareImage?.annotations} />;
              }

              // Side-by-side mode: always show TransformableImages
              return <div className="flex h-full gap-2">
                      <div className="flex-1 rounded-lg">
                        <TransformableImage imageUrl={sourceUrl} imageName={currentSourceImage?.name || currentSourceImage?.originalFilename} label="Before" annotations={currentSourceImage?.annotations} transform={beforeTransform} onTransformChange={setBeforeTransform} onSelect={() => {
                    setSelectedImage("before");
                    setAfterCropMode(false);
                  }} cropMode={beforeCropMode} pendingCrop={beforePendingCrop} onPendingCropChange={setBeforePendingCrop} />
                      </div>
                      <div className="flex-1 rounded-lg">
                        <TransformableImage imageUrl={compareUrl} imageName={selectedCompareImage.name || selectedCompareImage.originalFilename} label="After" annotations={selectedCompareImage.annotations} transform={afterTransform} onTransformChange={setAfterTransform} onSelect={() => {
                    setSelectedImage("after");
                    setBeforeCropMode(false);
                  }} cropMode={afterCropMode} pendingCrop={afterPendingCrop} onPendingCropChange={setAfterPendingCrop} />
                      </div>
                    </div>;
            })() :
            // Show source on left, placeholder on right when no comparison selected
            <div className="flex h-full gap-2">
                  <div className="relative flex-1 overflow-hidden rounded-lg bg-black">
                    {currentSourceImage?.signedUrl || currentSourceImage?.url ? <Image src={currentSourceImage.signedUrl || currentSourceImage.url || ""} alt={currentSourceImage.name || currentSourceImage.originalFilename || ""} fill className="object-contain" unoptimized priority sizes="50vw" onLoad={() => reportScribeAttachmentLoaded(false)} /> : <div className="flex h-full items-center justify-center text-foreground-muted">
                        No image available
                      </div>}
                    {/* Minimal corner label */}
                    <div className="absolute left-0 top-0">
                      <span className="bg-black/70 px-1 text-tiny text-white">
                        Before
                      </span>
                    </div>
                  </div>
                  <Card className="flex flex-1 flex-col items-center justify-center border-2 border-dashed">
                    <HiOutlinePhotograph className="mb-3 h-12 w-12 text-foreground-muted" />
                    <p className="text-body-strong text-muted-foreground">
                      Select an &quot;After&quot; image
                    </p>
                    <p className="mt-1 text-caption text-foreground-muted">
                      <span className="md:hidden">
                        Choose from the panel below
                      </span>
                      <span className="hidden md:inline">
                        Choose from the panel on the right
                      </span>
                    </p>
                  </Card>
                </div>}
            </div>
          </div>

          {/* Image selector panel */}
          <Card className="flex h-[160px] w-full flex-shrink-0 flex-col p-0 sm:h-[200px] md:h-auto md:w-72">
            <div className="flex flex-shrink-0 items-center gap-3 border-b p-2 md:flex-col md:items-stretch md:gap-0 md:p-3">
              <Tabs value={selectorMode} onValueChange={v_1 => setSelectorMode(v_1 as "before" | "after")}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="before">Before</TabsTrigger>
                  <TabsTrigger value="after">After</TabsTrigger>
                </TabsList>
              </Tabs>
              <p className="whitespace-nowrap text-caption text-muted-foreground md:mt-2">
                {selectorMode === "before" ? <span>{availableImages.length}</span> : <span>{attachments.length}</span>}
                <span> </span>
                <span>image</span>
                {(selectorMode === "before" ? availableImages.length : attachments.length) !== 1 ? <span>{"s"}</span> : <span>{""}</span>}
                <span> </span>
                <span>available</span>
              </p>
            </div>

            {/* Search */}
            {(selectorMode === "before" && availableImages.length > 6 || selectorMode === "after" && attachments.length > 6) && <div className="flex-shrink-0 border-b p-3">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
                  <Input placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
                </div>
              </div>}

            {/* Image grid */}
            <div className="flex-1 overflow-y-auto p-2 md:p-3">
              {(() => {
              // Determine which images to show based on selector mode
              const imagesToShow = selectorMode === "before" ? availableImages.filter(a_1 => {
                const term_0 = searchTerm.toLowerCase();
                if (!term_0.trim()) return true;
                const name_0 = (a_1.name || a_1.originalFilename || "").toLowerCase();
                const patientName_0 = a_1.patient ? `${a_1.patient.firstName} ${a_1.patient.lastName}`.toLowerCase() : "";
                return name_0.includes(term_0) || patientName_0.includes(term_0);
              }) : filteredAttachments;

              // Group images by date
              const groups: {
                [date: string]: NoteAttachment[];
              } = {};
              imagesToShow.forEach(a_2 => {
                let dateKey = "Unknown";
                if (a_2.createdDate) {
                  try {
                    const date = new Date(a_2.createdDate);
                    if (!isNaN(date.getTime())) {
                      dateKey = toClinicDateKey(date);
                    }
                  } catch {
                    // Invalid date, use "Unknown"
                  }
                }
                if (!groups[dateKey]) {
                  groups[dateKey] = [];
                }
                groups[dateKey].push(a_2);
              });
              const groupedImages = Object.entries(groups).sort(([a_3], [b]) => {
                if (a_3 === "Unknown") return 1;
                if (b === "Unknown") return -1;
                return b.localeCompare(a_3);
              });
              const loadMoreSentinel = canLoadMore ? <div ref={setLoadMoreObserver} className="py-4 text-center text-caption text-foreground-muted">
                    {isLoadingMoreImages ? <LuLoader2 className="mx-auto h-4 w-4 animate-spin" /> : "Scroll to load more"}
                  </div> : null;
              if (imagesToShow.length === 0) {
                return <>
                      <div className="flex flex-col items-center justify-center py-4 text-center md:py-8">
                        <HiOutlinePhotograph className="mb-2 h-6 w-6 text-foreground-muted md:h-8 md:w-8" />
                        <p className="text-body text-muted-foreground">
                          No images available
                        </p>
                      </div>
                      {loadMoreSentinel}
                    </>;
              }
              return <div className="space-y-3 md:space-y-4">
                    {groupedImages.map(([date_0, items]) => <div key={date_0}>
                        <p className="mb-1.5 text-caption-strong text-muted-foreground md:mb-2">
                          {formatDate(date_0, {
                      year: "numeric",
                      month: "short",
                      day: "numeric"
                    })}
                        </p>
                        <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-5 md:grid-cols-3 md:gap-2">
                          {items.map(attachment_0 => {
                      const isSelected = selectorMode === "before" ? currentSourceImage?.id === attachment_0.id : selectedCompareImage?.id === attachment_0.id;
                      return <ImageThumbnail key={attachment_0.id} attachment={attachment_0} isSelected={isSelected} onClick={() => {
                        if (selectorMode === "before") {
                          setSelectedSourceImage(attachment_0);
                        } else {
                          setSelectedCompareImage(attachment_0);
                        }
                      }} />;
                    })}
                        </div>
                      </div>)}
                    {loadMoreSentinel}
                  </div>;
            })()}
            </div>
          </Card>
        </DialogBody>
      </DialogContent>

      {/* Patient Selector Modal for Save */}
      <Dialog open={patientSelectorOpen} onOpenChange={setPatientSelectorOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Patient</DialogTitle>
            <DialogDescription>
              Please select a patient to save the comparison image to their
              gallery.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <PatientSearch patient={selectedPatientForSave} onPatientSelect={patient => {
            setSelectedPatientForSave(patient);
          }} placeholder="Search patient by phone, id, name or dob" className="w-full" />
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => {
            setPatientSelectorOpen(false);
            setSelectedPatientForSave(undefined);
          }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => {
            if (!selectedPatientForSave) return;
            setPatientSelectorOpen(false);
            setSelectedPatientForSave(undefined);
            performSave(selectedPatientForSave.id);
          }} disabled={!selectedPatientForSave || isSaving} isLoading={isSaving}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>;
}
function _temp(point_0, index_0) {
  return `${index_0 === 0 ? "M" : "L"} ${point_0.x} ${point_0.y}`;
}
function _temp2(point, index) {
  return `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`;
}
function _temp3() {
  return reportScribeAttachmentLoaded(false);
}
function _temp4() {
  return reportScribeAttachmentLoaded(false);
}
function _temp5(touches) {
  if (touches.length < 2) {
    return 0;
  }
  const dx = touches[1].clientX - touches[0].clientX;
  const dy = touches[1].clientY - touches[0].clientY;
  return Math.hypot(dx, dy);
}
function _temp6() {
  return reportScribeAttachmentLoaded(false);
}